package com.upcominds.expedite.data.auth

import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlin.js.Promise

actual class OidcAuthPlatformImpl actual constructor(
    private val config: OidcConfig
) : OidcAuthPlatform {

    private var keycloak: dynamic = null
    private var initPromise: Promise<Boolean>? = null
    private var wasCallback = false

    init {
        try {
            val href = window.location.href
            wasCallback =
                href.contains("code=") ||
                        href.contains("state=") ||
                        href.contains("error=")

            if (!wasCallback) {
                clearKeycloakStorage()
            }

            val keycloakConfig = js("{}")
            keycloakConfig.url = config.issuer.substringBefore("/realms")
            keycloakConfig.realm = config.issuer.substringAfterLast("/")
            keycloakConfig.clientId = config.clientId

            val KeycloakConstructor = js("require('keycloak-js')")
            keycloak = if (KeycloakConstructor.default != undefined) {
                js("new KeycloakConstructor.default(keycloakConfig)")
            } else {
                js("new KeycloakConstructor(keycloakConfig)")
            }

            val initOptions = js("{}")
            // initOptions.onLoad = "check-sso" // Removed to rely on code detection or manual login
            initOptions.pkceMethod = "S256"
            initOptions.checkLoginIframe = false
            initOptions.enableLogging = true
            // Dev workaround: avoid nonce mismatch errors that cause init to fail
            initOptions.useNonce = false

            println(
                "Initializing Keycloak: " +
                        "url=${keycloakConfig.url}, " +
                        "realm=${keycloakConfig.realm}, " +
                        "clientId=${keycloakConfig.clientId}"
            )

            val p = keycloak.init(initOptions) as Promise<Boolean>
            initPromise = p

            p.then { authenticated ->
                if (authenticated == true) {
                    println("Keycloak authenticated")
                    saveTokens()
                } else {
                    println("Keycloak not authenticated")
                }
                true
            }.catch { error ->
                console.error("Keycloak init failed", error)
                false
            }

        } catch (e: dynamic) {
            console.error("Failed to initialize keycloak-js", e)
        }
    }

    override suspend fun startLogin(): TokenSet {
        if (keycloak == null) {
            throw IllegalStateException("Keycloak not initialized")
        }

        val authenticated = suspendCoroutine<Boolean> { cont ->
            initPromise
                ?.then { cont.resume(it) }
                ?.catch { cont.resume(false) }
                ?: cont.resume(false)
        }

        if (authenticated) {
            println("Already authenticated")
            return tokenSet()
        }

        if (wasCallback) {
            wasCallback = false

            // καθαρίζουμε το URL από code/state
            window.history.replaceState(
                null,
                "",
                window.location.pathname
            )

            throw IllegalStateException(
                "Login callback processed but authentication failed"
            )
        }

        println("Redirecting to Keycloak login")
        val loginOptions = js("{}")
        loginOptions.redirectUri = window.location.origin + window.location.pathname
        keycloak.login(loginOptions)

        // suspend forever – browser κάνει redirect
        return suspendCoroutine { }
    }

    override suspend fun logout() {
        val options = js("{}")
        options.redirectUri = window.location.origin
        keycloak.logout(options)
    }

    private fun saveTokens() {
        val token = keycloak.token as? String ?: return

        val rawExp = keycloak.tokenParsed?.exp
        val exp = when (rawExp) {
            is Int -> rawExp.toLong()
            is Double -> rawExp.toLong()
            else -> 0L
        }

        localStorage.setItem("access_token", token)

        val refreshToken = keycloak.refreshToken as? String
        if (refreshToken != null) {
            localStorage.setItem("refresh_token", refreshToken)
        }

        val idToken = keycloak.idToken as? String
        if (idToken != null) {
            localStorage.setItem("id_token", idToken)
        }

        localStorage.setItem("expires_at_epoch_seconds", exp.toString())

        println("Tokens saved, exp=$exp")
    }

    private fun tokenSet(): TokenSet {
        val rawExp = keycloak.tokenParsed?.exp
        val exp = when (rawExp) {
            is Int -> rawExp.toLong()
            is Double -> rawExp.toLong()
            else -> 0L
        }

        return TokenSet(
            accessToken = keycloak.token as String,
            refreshToken = keycloak.refreshToken as? String,
            idToken = keycloak.idToken as? String,
            expiresAtEpochSeconds = exp
        )
    }

    private fun clearKeycloakStorage() {
        // Clear keycloak adapter leftovers to avoid nonce/state mismatches
        val keysToRemove = mutableListOf<String>()
        for (i in 0 until localStorage.length) {
            val key = localStorage.key(i)
            if (key != null && (key.startsWith("kc-") || key.contains("keycloak"))) {
                keysToRemove.add(key)
            }
        }
        keysToRemove.forEach { localStorage.removeItem(it) }

        // Also clear sessionStorage keys created by keycloak-js
        val sessionKeysToRemove = mutableListOf<String>()
        for (i in 0 until window.sessionStorage.length) {
            val key = window.sessionStorage.key(i)
            if (key != null && (key.startsWith("kc-") || key.contains("keycloak"))) {
                sessionKeysToRemove.add(key)
            }
        }
        sessionKeysToRemove.forEach { window.sessionStorage.removeItem(it) }
    }
}
